import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshCwIcon,
  SearchIcon,
  StarIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useNavigationType } from 'react-router';

import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { fillViewportSx } from '@/components/credit-contracts/layout';
import { MonthGrid } from '@/components/credit-contracts/MonthPicker';
import { compactTableSx, stickyCell, stickyRowSx } from '@/components/credit-contracts/tableParts';
import { reflectedText } from '@/components/credit-consumption/display';
import { GroupedHeader, type HeaderColumn } from '@/components/credit-consumption/GroupedHeader';
import {
  companySortValue,
  listColumns,
  type ListColumn,
} from '@/components/credit-consumption/listColumns';
import { nextSort, sortRows, type SortState } from '@/components/credit-consumption/sorting';
import {
  groupLabels,
  useConsumptionList,
} from '@/components/credit-consumption/useConsumptionList';
import type { CompanyRow } from '@/lib/api/credit-consumption';
import { addMonths, monthLabel } from '@/lib/credit-contracts/month';

/** 最後に消費詳細を開いた企業。ブラウザの「戻る」で消費一覧に戻ったときに、その企業の行が見える位置にする */
let lastOpenedCompany: string | null = null;

/**
 * 消費詳細のパス。指定した月を month に渡し、消費詳細でその月を選んだ状態にする。
 * 戻るときに一覧の状態を移る前のままにするよう、消費一覧のクエリを list に持たせる
 */
const detailPath = (companyId: string, month: string, listSearch: string) => {
  const params = new URLSearchParams({ month });
  const list = listSearch.replace(/^\?/, '');
  if (list) params.set('list', list);
  return `/credit/consumption/${encodeURIComponent(companyId)}?${params.toString()}`;
};

/**
 * 消費一覧（FKV3-13046）。サイドバー「クレジット制」>「消費一覧」。
 * 指定した月について、全企業を1行ずつ並べ、契約との対比、過去6か月の消費、時点値とデータコネクト、消費の内訳を表示する。
 * 表は画面の幅と高さいっぱいに使い、見出しの行を固定して値の行だけをスクロールする。
 */
export function ConsumptionListPage() {
  const s = useConsumptionList();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const data = s.list.status === 'ok' ? s.list.data : null;
  const [monthOpen, setMonthOpen] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const restoreTarget = useRef<string | null>(
    (location.state as { focusCompany?: string } | null)?.focusCompany ??
      (navigationType === 'POP' ? lastOpenedCompany : null),
  );
  const [opened, setOpened] = useState<string | null>(restoreTarget.current);

  const columns = useMemo(() => (data ? listColumns(data) : []), [data]);
  const shown = useMemo(() => {
    const col = columns.find((c) => c.key === s.sort.key);
    return sortRows(
      s.filtered,
      (row) =>
        companySortValue(s.sort.key, row) ?? col?.sortValue(row) ?? { kind: 'code', value: row.id },
      s.sort.dir,
    );
  }, [s.filtered, columns, s.sort]);

  useEffect(() => {
    const target = restoreTarget.current;
    if (!target || s.list.status !== 'ok' || s.refreshing) return;
    restoreTarget.current = null;
    const row = Array.from(
      tableRef.current?.querySelectorAll<HTMLElement>('[data-company-id]') ?? [],
    ).find((e) => e.dataset.companyId === target);
    row?.scrollIntoView?.({ block: 'center' });
  }, [s.list, s.refreshing, shown]);

  const markOpened = (companyId: string) => {
    lastOpenedCompany = companyId;
    setOpened(companyId);
  };

  const scrollToTop = () => {
    if (tableRef.current) tableRef.current.scrollTop = 0;
  };

  const monthDisabled = !data || s.refreshing;
  const first = data ? (data.first_month ?? data.current_month) : null;

  return (
    <AppLayout title="消費一覧" showSearch={false} disableContentPadding>
      <Box display="flex" flexDirection="column" gap={1.5} sx={fillViewportSx}>
        <Box
          display="flex"
          alignItems="center"
          columnGap={3}
          rowGap={1}
          flexWrap="wrap"
          flexShrink={0}
        >
          <Box display="flex" alignItems="center" gap={0.5} role="group" aria-label="月">
            <IconButton
              size="small"
              aria-label="前の月"
              disabled={monthDisabled || !data || !first || data.month <= first}
              onClick={() => data && s.selectMonth(addMonths(data.month, -1))}
            >
              <ChevronLeftIcon size={18} />
            </IconButton>
            <Popover open={monthOpen} onOpenChange={setMonthOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-w-[112px] tabular-nums"
                  aria-label={data ? `月を選ぶ（${monthLabel(data.month)}）` : '月を選ぶ'}
                  disabled={monthDisabled}
                >
                  <span aria-live="polite">{data ? monthLabel(data.month) : ''}</span>
                  <CalendarIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-3">
                {data && first && (
                  <MonthGrid
                    value={data.month}
                    current={data.current_month}
                    min={first}
                    max={data.current_month}
                    onSelect={(month) => {
                      setMonthOpen(false);
                      s.selectMonth(month);
                    }}
                  />
                )}
              </PopoverContent>
            </Popover>
            <IconButton
              size="small"
              aria-label="次の月"
              disabled={monthDisabled || !data || data.month >= data.current_month}
              onClick={() => data && s.selectMonth(addMonths(data.month, 1))}
            >
              <ChevronRightIcon size={18} />
            </IconButton>
            <Button
              size="sm"
              variant="outline"
              aria-label="当月に戻す"
              disabled={!data || s.refreshing || data.month === data.current_month}
              onClick={() => data && s.selectMonth(data.current_month)}
            >
              当月
            </Button>
          </Box>
          <TextField
            size="small"
            placeholder="企業名・企業ID"
            value={s.query}
            onChange={(e) => {
              s.setQuery(e.target.value);
              scrollToTop();
            }}
            slotProps={{
              htmlInput: { 'aria-label': '企業名か企業IDで絞り込む' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={16} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: 240 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={s.favoritesOnly}
                onChange={(e) => {
                  s.setFavoritesOnly(e.target.checked);
                  scrollToTop();
                }}
              />
            }
            label="お気に入りのみ表示"
          />
          {data && (
            <Box display="flex" columnGap={2} ml="auto">
              <Typography variant="body2" color="text.secondary">
                反映時点 {reflectedText(data.reflected_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {shown.length} / {data.companies.length}社
              </Typography>
            </Box>
          )}
        </Box>

        {s.list.status === 'loading' && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}
        {s.list.status === 'error' && (
          <Alert
            severity="error"
            action={
              <Button size="sm" variant="outline" onClick={s.reload}>
                <RefreshCwIcon />
                再読み込み
              </Button>
            }
          >
            {s.list.message}
          </Alert>
        )}
        {data && (
          <ListTable
            containerRef={tableRef}
            month={data.month}
            columns={columns}
            rows={shown}
            sort={s.sort}
            onSort={(key, numeric) => s.setSort(nextSort(s.sort, key, numeric))}
            refreshing={s.refreshing}
            opened={opened}
            listSearch={location.search}
            toggling={s.toggling}
            emptyMessage={
              s.favoritesOnly && !data.companies.some((c) => c.favorite)
                ? 'お気に入りの企業はまだありません。☆を押すと登録できます。'
                : '条件に合う企業はありません。'
            }
            onToggleFavorite={(c) => void s.toggleFavorite(c)}
            onOpen={(c) => {
              markOpened(c.id);
              navigate(detailPath(c.id, data.month, location.search));
            }}
            onOpenedByLink={markOpened}
          />
        )}
      </Box>
    </AppLayout>
  );
}

const FAVORITE_WIDTH = 48;
const ID_WIDTH = 140;
const NAME_WIDTH = { min: 200 };
const NAME_MAX_WIDTH = 320;
const favoriteCol = (header?: boolean) => {
  const cell = stickyCell('left', 0, FAVORITE_WIDTH, { header });
  return { ...cell, sx: { ...(cell.sx as object), px: 0, textAlign: 'center' } };
};
const idCol = (header?: boolean) => stickyCell('left', FAVORITE_WIDTH, ID_WIDTH, { header });
const nameCol = (header?: boolean) =>
  stickyCell('left', FAVORITE_WIDTH + ID_WIDTH, NAME_WIDTH, { header, edge: true });

const numberCell = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

interface ListTableProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  month: string;
  columns: ListColumn[];
  rows: CompanyRow[];
  sort: SortState;
  onSort: (key: string, numeric: boolean) => void;
  refreshing: boolean;
  opened: string | null;
  listSearch: string;
  toggling: ReadonlySet<string>;
  emptyMessage: string;
  onToggleFavorite: (company: CompanyRow) => void;
  onOpen: (company: CompanyRow) => void;
  onOpenedByLink: (companyId: string) => void;
}

/**
 * 企業ごとの、指定した月の値の表。行を選ぶと消費詳細に移る。企業名はリンクで、新しいタブでも開ける。
 * 幅が足りないときは、お気に入り・企業ID・企業名を固定して横にスクロールする
 */
function ListTable({
  containerRef,
  month,
  columns,
  rows,
  sort,
  onSort,
  refreshing,
  opened,
  listSearch,
  toggling,
  emptyMessage,
  onToggleFavorite,
  onOpen,
  onOpenedByLink,
}: ListTableProps) {
  const header: HeaderColumn[] = [
    {
      key: 'favorite',
      path: ['お気に入り'],
      cell: favoriteCol(true),
      ariaLabel: 'お気に入り',
      content: (
        <Box display="flex" justifyContent="center">
          <StarIcon size={14} aria-hidden />
        </Box>
      ),
    },
    { key: 'id', path: ['企業ID'], cell: idCol(true), sortable: true },
    { key: 'name', path: ['企業名'], cell: nameCol(true), sortable: true },
    ...columns.map((c) => ({
      key: c.key,
      path: [groupLabelOf(c), ...(c.category ? [c.category] : []), c.name],
      pathHints: c.categoryHint ? [undefined, c.categoryHint] : undefined,
      unit: c.unit,
      numeric: c.numeric,
      sortable: true,
    })),
  ];
  return (
    <Box position="relative" flex="0 1 auto" minHeight={0} display="flex" flexDirection="column">
      {refreshing && (
        <LinearProgress
          aria-label="取得中"
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 4 }}
        />
      )}
      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto' }}
      >
        <Table
          stickyHeader
          size="small"
          aria-label={`${monthLabel(month)}の消費`}
          sx={compactTableSx}
        >
          <TableHead>
            <GroupedHeader columns={header} sort={sort} onSort={onSort} />
          </TableHead>
          <TableBody sx={{ opacity: refreshing ? 0.5 : 1 }}>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + 3}>
                  <Typography variant="body2" color="text.secondary" py={1}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((c) => (
              <TableRow
                key={c.id}
                hover
                selected={opened === c.id}
                data-company-id={c.id}
                sx={[{ cursor: 'pointer' }, stickyRowSx]}
                onClick={() => onOpen(c)}
              >
                <TableCell {...favoriteCol()}>
                  <Box display="flex" justifyContent="center">
                    <IconButton
                      size="small"
                      sx={{ p: 0.25 }}
                      aria-pressed={c.favorite}
                      aria-label={`${c.name}をお気に入り${c.favorite ? 'から外す' : 'に登録する'}`}
                      disabled={toggling.has(c.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(c);
                      }}
                    >
                      <StarIcon
                        size={18}
                        className={c.favorite ? 'fill-amber-400 text-amber-500' : 'text-gray-400'}
                      />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell {...idCol()}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {c.id}
                  </Typography>
                </TableCell>
                <TableCell {...nameCol()} title={c.name}>
                  <MuiLink
                    component={RouterLink}
                    to={detailPath(c.id, month, listSearch)}
                    underline="hover"
                    variant="body2"
                    fontWeight="bold"
                    noWrap
                    display="block"
                    maxWidth={NAME_MAX_WIDTH}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onOpenedByLink(c.id);
                    }}
                  >
                    {c.name}
                  </MuiLink>
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.key} sx={col.numeric ? numberCell : undefined}>
                    {col.render(c)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const groupLabelOf = (c: ListColumn) => groupLabels[c.group];
